/**
 * Static copy for a plan term's usage limit, e.g. the 24k miles of a "24 month / 24k mile" term.
 */
export const PLAN_TERM_USAGE_LIMIT_CONFIG = {
  title: 'Usage Limit',
  description:
    'Coverage ends once the asset has been used this much since the sale, or at the end of the term, whichever comes first.',
  hint: "Checked on claims when the policy's claim eligibility check is on.",
  empty: 'No usage limit for this term',
  emptyHint: 'Coverage lasts for the whole term',
  add: 'Usage limit',
  remove: 'Remove usage limit',
  reading: 'Reading',
  readingPlaceholder: 'Select a reading',
  noReadings: "The policy's asset type has no number dimensions to read usage from.",
  limit: 'Most use since the sale',
  limitIn: (reading: string) => `Most use since the sale, in ${reading}`,

  // Why Save is off.
  problems: {
    reading: 'Pick the reading the limit is measured on.',
    values: 'Fill in every limit, and give each if statement a condition.',
  },

  // When the policy's claim eligibility check is off, a limit wouldn't be checked.
  off: {
    label: 'Usage limits',
    summary: 'Off · turn on the claim eligibility check on the policy',
    notChecked: "not checked while the policy's check is off",
    title: 'Usage limits are off for this policy',
    hint: 'Turn on the claim eligibility check on the policy to set one.',
    savedButOff: "A limit is saved, but it isn't checked while the policy's check is off.",
  },

  // The plan page's per-term card, which shows both the conditions and the usage limit.
  termSettingsLabel: 'Term Settings',

  detailsLabel: 'Usage limit',
  none: 'None',
  summary: (limit: string, reading: string) => `${limit} since the sale, on ${reading}`,
  summaryFromAsset: (field: string, reading: string) => `From ${field}, since the sale, on ${reading}`,
  conditional: (count: number) => `varies by ${count} if ${count === 1 ? 'statement' : 'statements'}`,

  // The term dialog edits both the qualifying conditions and the usage limit.
  dialog: {
    title: 'Edit Term',
    subtitle: (term: string, policy: string) =>
      `Edit qualifying conditions and the usage limit for <strong><i>${term}</i></strong> on <strong><i>${policy}</i></strong>`,
    fallbackSubtitle: 'Edit term-level qualifying conditions and usage limit',
    // With the tenant's new claims flow off there are no usage limits to edit.
    conditionsSubtitle: (term: string, policy: string) =>
      `Edit qualifying conditions for <strong><i>${term}</i></strong> on <strong><i>${policy}</i></strong>`,
    conditionsFallbackSubtitle: 'Edit term-level qualifying conditions',
  },

  // Titles are HTML-escaped by the caller.
  toasts: {
    savedTitle: 'Updated term',
    saved: (term: string) => `Saved changes to <strong>${term}</strong>.`,
    newVersionTitle: 'New policy version created',
    newVersion: (term: string) => `Saved changes to <strong>${term}</strong> and created a new policy version.`,
  },
} as const;
